import { useEffect, useMemo, useState } from 'react';
import { ACHIEVEMENT_BY_CODE, TIER_POINTS } from '@numslearn/shared';
import { useSettings } from '../settings.js';
import { playBadge } from '../sound.js';
import type { StringKey } from '../i18n/index.js';

/**
 * The badge celebration.
 *
 * A badge that slides in at the corner is missed by a class watching a board;
 * this takes the screen for two and a half seconds, because the moment IS the
 * reward. Badges queue rather than overlap — two at once would read as neither.
 */

const HOLD_MS = 2900;

/** Fixed spread so the confetti reads as a burst, not as noise. */
const CONFETTI = Array.from({ length: 34 }, (_, i) => {
  const angle = (i / 34) * Math.PI * 2 + (i % 3) * 0.21;
  const dist = 130 + ((i * 37) % 130);
  return {
    dx: `${Math.round(Math.cos(angle) * dist)}px`,
    dy: `${Math.round(Math.sin(angle) * dist - 40)}px`,
    rot: `${((i * 73) % 720) - 360}deg`,
    delay: `${(i % 6) * 26}ms`,
    colour: `var(--g-c${(i % 4) + 1})`,
    round: i % 3 === 0,
  };
});

export function BadgeBurst({ queue, onShown }: { queue: string[]; onShown: (code: string) => void }) {
  const [current, setCurrent] = useState<string | null>(null);

  // One at a time, in the order they were earned.
  useEffect(() => {
    if (current !== null || queue.length === 0) return;
    setCurrent(queue[0]!);
  }, [current, queue]);

  useEffect(() => {
    if (current === null) return;
    playBadge();
    const id = window.setTimeout(() => {
      onShown(current);
      setCurrent(null);
    }, HOLD_MS);
    return () => window.clearTimeout(id);
  }, [current, onShown]);

  if (current === null) return null;
  return (
    <BadgeCard
      code={current}
      remaining={Math.max(0, queue.length - 1)}
      onDismiss={() => { onShown(current); setCurrent(null); }}
    />
  );
}

function BadgeCard({ code, remaining, onDismiss }: { code: string; remaining: number; onDismiss: () => void }) {
  const { t, num } = useSettings();
  const ach = ACHIEVEMENT_BY_CODE.get(code);
  const reduced = useMemo(
    () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  if (!ach) return null;

  return (
    <div className="burst" role="status" aria-live="assertive" onClick={onDismiss}>
      <div className="burst-card" data-tier={ach.tier}>
        <div className="burst-medal-wrap">
          {!reduced && <span className="burst-rays" aria-hidden="true" />}
          <span className="burst-medal" aria-hidden="true">{ach.icon}</span>
          {!reduced && CONFETTI.map((c, i) => (
            <span
              key={i}
              className={`confetti${c.round ? ' is-round' : ''}`}
              aria-hidden="true"
              style={{
                background: c.colour,
                animationDelay: c.delay,
                ['--dx' as string]: c.dx,
                ['--dy' as string]: c.dy,
                ['--rot' as string]: c.rot,
              }}
            />
          ))}
        </div>

        <span className="burst-kicker">{t('ach.unlocked')}</span>
        <strong className="burst-name">{t(ach.nameKey as StringKey)}</strong>
        <span className="burst-desc">{t(ach.descKey as StringKey)}</span>

        <span className="burst-meta">
          <span className="burst-tier" data-tier={ach.tier}>{t(`tier.${ach.tier}` as StringKey)}</span>
          <span className="burst-pts">{t('ach.points', { n: TIER_POINTS[ach.tier] })}</span>
        </span>

        {remaining > 0 && <span className="burst-more">+{num(remaining)}</span>}
      </div>
    </div>
  );
}
